import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, Empty } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LedgerDialog, RefundDialog, RefundPolicyCard } from "@/components/admin/OrderLedger";

const Table = ({ head, children, empty }) => (
  <div className="panel overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[#F4F6FA] text-left text-xs uppercase tracking-wider text-[#475569]"><tr>{head.map((h) => <th key={h} className="whitespace-nowrap p-3">{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
    {empty && <p className="p-6 text-sm text-[#475569]">Nothing here yet.</p>}
  </div>
);

export function Students() {
  const [q, setQ] = useState("");
  const { data, loading } = useFetch(`/admin/students?q=${encodeURIComponent(q)}`);
  return (
    <>
      <PageTitle title="Students" />
      <Input className="mb-3 max-w-xs bg-white" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="students-search-input" />
      {loading ? <Loader /> : (
        <Table head={["Name", "Email", "Phone", "Tickets", "Joined"]} empty={data.length === 0}>
          {data.map((u) => (
            <tr key={u.id} className="border-t" data-testid={`student-row-${u.id}`}>
              <td className="p-3"><Link to={`/superadmin/students/${u.id}`} className="font-semibold underline" data-testid={`student-link-${u.id}`}>{u.name}</Link></td>
              <td className="p-3">{u.email}</td><td className="p-3">{u.phone || "–"}</td><td className="p-3 font-mono-num">{u.tickets}</td><td className="p-3">{fmtDate(u.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

export function StudentDetail() {
  const { id } = useParams();
  const { data: d, loading, reload } = useFetch(`/admin/students/${id}`);
  if (loading) return <Loader />;
  const issue = async () => { try { await api.post(`/admin/students/${id}/certificate`); toast.success("Certificate issued"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <PageTitle title={d.user.name} sub={`${d.user.email} · ${d.user.phone || "no phone"} · joined ${fmtDate(d.user.created_at)}`}>
        {d.certificates.length === 0 && <button className="btn-gold" onClick={issue} data-testid="issue-certificate-btn">Issue certificate</button>}
      </PageTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel p-5"><h3 className="font-display text-xl font-bold uppercase">Orders</h3>{d.orders.map((o) => <div key={o.id} className="mt-2 flex justify-between border-t pt-2 text-sm"><span>{o.number} · {o.event_title}</span><span className="font-mono-num">{money(o.total_cents)} · {o.status}</span></div>)}{d.orders.length === 0 && <p className="mt-2 text-sm text-[#475569]">None</p>}</div>
        <div className="panel p-5"><h3 className="font-display text-xl font-bold uppercase">Tickets</h3>{d.registrations.map((r) => <div key={r.id} className="mt-2 flex justify-between border-t pt-2 text-sm"><span>{r.attendee_name} · {r.event_title}</span><span className="font-mono-num">{r.ticket_code} · {r.status}</span></div>)}{d.registrations.length === 0 && <p className="mt-2 text-sm text-[#475569]">None</p>}</div>
        <div className="panel p-5"><h3 className="font-display text-xl font-bold uppercase">Quiz attempts</h3>{d.attempts.map((a) => <div key={a.id} className="mt-2 flex justify-between border-t pt-2 text-sm"><span>{a.quiz_slug}</span><span className={`font-mono-num ${a.passed ? "text-[#1E8E5A]" : "text-[#C8382E]"}`}>{a.score}%</span></div>)}{d.attempts.length === 0 && <p className="mt-2 text-sm text-[#475569]">None</p>}</div>
        <div className="panel p-5"><h3 className="font-display text-xl font-bold uppercase">Badges, certificates, support</h3>
          <p className="mt-2 text-sm">Badges: {d.badges.map((b) => b.badge_slug).join(", ") || "none"}</p>
          <p className="mt-1 text-sm">Certificates: {d.certificates.map((c) => c.code).join(", ") || "none"}</p>
          <p className="mt-1 text-sm">Tickets: {d.tickets.map((t) => `#${t.number} (${t.status})`).join(", ") || "none"}</p>
        </div>
      </div>
    </>
  );
}

export function Orders() {
  const [status, setStatus] = useState("");
  const { data, loading, reload } = useFetch(`/admin/orders${status ? `?status=${status}` : ""}`);
  const [refund, setRefund] = useState(null);
  const [ledger, setLedger] = useState(null);
  return (
    <>
      <PageTitle title="Orders and refunds">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border bg-white px-3 text-sm" data-testid="orders-status-filter">
          <option value="">All</option>{["paid", "pending", "refunded", "partially_refunded", "expired"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </PageTitle>
      <RefundPolicyCard />
      {loading ? <Loader /> : (
        <Table head={["Order", "Buyer", "Event", "Qty", "Total", "Tax", "Status", "Date", ""]} empty={data.length === 0}>
          {data.map((o) => (
            <tr key={o.id} className="border-t" data-testid={`order-row-${o.id}`}>
              <td className="p-3 font-mono-num">{o.number}</td><td className="p-3">{o.user_email}</td><td className="p-3">{o.event_title}</td><td className="p-3 font-mono-num">{o.quantity}</td>
              <td className="p-3 font-mono-num">{money(o.total_cents)}</td><td className="p-3 font-mono-num">{money(o.tax_cents)}</td><td className="p-3 uppercase">{o.status.replace("_", " ")}</td><td className="p-3">{fmtDate(o.created_at)}</td>
              <td className="whitespace-nowrap p-3"><button className="mr-2 text-xs underline" onClick={() => setLedger(o)} data-testid={`ledger-btn-${o.id}`}>Ledger</button>{["paid", "partially_refunded"].includes(o.status) && <button className="btn-outline px-2 py-1 text-xs" onClick={() => setRefund(o)} data-testid={`refund-btn-${o.id}`}>Refund</button>}</td>
            </tr>
          ))}
        </Table>
      )}
      {ledger && <LedgerDialog order={ledger} onClose={() => setLedger(null)} />}
      {refund && <RefundDialog order={refund} onClose={() => setRefund(null)} onDone={() => { setRefund(null); reload(); }} />}
    </>
  );
}

export function Leads() {
  const { data, loading } = useFetch("/admin/leads");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Leads and waitlists" sub="Free seminar sign-ups, waitlists, newsletter and quiz results." />
      <Table head={["Type", "Name", "Email", "City", "Result", "Consent", "Date"]} empty={data.length === 0}>
        {data.map((l) => (
          <tr key={l.id} className="border-t" data-testid={`lead-row-${l.id}`}>
            <td className="p-3 uppercase">{l.type}</td><td className="p-3">{l.name || "–"}</td><td className="p-3">{l.email}</td><td className="p-3">{l.city_slug || "–"}</td><td className="p-3">{l.result || "–"}</td><td className="p-3">{l.consent ? "Yes" : "No"}</td><td className="p-3">{fmtDate(l.created_at)}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}

export function SupportDesk() {
  const [status, setStatus] = useState("open");
  const { data, loading, reload } = useFetch(`/admin/support${status ? `?status=${status}` : ""}`);
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const t = (data || []).find((x) => x.id === sel);
  const send = async (newStatus) => {
    try {
      await api.post(`/admin/support/${sel}/reply`, { message: msg, internal, status: newStatus || null });
      setMsg(""); toast.success(internal ? "Note added" : "Reply sent"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setSt = async (s) => { await api.put(`/admin/support/${sel}`, { status: s }); reload(); };
  return (
    <>
      <PageTitle title="Support desk">
        <div className="flex gap-1">{["open", "pending", "resolved", ""].map((s) => <button key={s} onClick={() => setStatus(s)} className={`rounded-md px-3 py-1.5 text-sm ${status === s ? "bg-[#0B1B33] text-white" : "bg-white"}`} data-testid={`support-filter-${s || "all"}`}>{s || "all"}</button>)}</div>
      </PageTitle>
      {loading ? <Loader /> : (
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="space-y-2 lg:col-span-2">
            {data.length === 0 && <Empty>No tickets.</Empty>}
            {data.map((x) => (
              <button key={x.id} onClick={() => setSel(x.id)} className={`panel block w-full p-3 text-left ${sel === x.id ? "border-[#E0A21B]" : ""}`} data-testid={`desk-ticket-${x.number}`}>
                <div className="flex justify-between text-xs"><span className="font-mono-num">#{x.number}</span><span className={`font-bold uppercase ${x.priority === "high" ? "text-[#C8382E]" : ""}`}>{x.priority} · {x.status}</span></div>
                <div className="mt-1 font-semibold">{x.subject}</div><div className="text-xs text-[#475569]">{x.name} · {x.email} · {fmtDateTime(x.updated_at)}</div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            {!t ? <Empty>Choose a ticket.</Empty> : (
              <div className="panel p-5" data-testid="desk-ticket-detail">
                <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-display text-2xl font-bold uppercase">#{t.number} {t.subject}</h3>
                  <select value={t.status} onChange={(e) => setSt(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="desk-status-select">{["open", "pending", "resolved", "closed"].map((s) => <option key={s}>{s}</option>)}</select></div>
                <div className="text-xs text-[#475569]">{t.name} · {t.email} · via {t.channel}{t.user_id && <> · <Link to={`/superadmin/students/${t.user_id}`} className="underline">Student 360</Link></>}</div>
                <div className="mt-4 max-h-96 space-y-2 overflow-y-auto">
                  {t.messages.map((m, i) => (
                    <div key={i} className={`rounded-md p-3 text-sm ${m.internal ? "border border-dashed border-[#E0A21B] bg-[#E0A21B]/10" : m.from === "staff" ? "bg-[#0B1B33] text-white" : "bg-[#F4F6FA]"}`}>
                      <div className="text-xs opacity-70">{m.internal ? "Internal note · " : ""}{m.author} · {fmtDateTime(m.at)}</div><div className="mt-1 whitespace-pre-line">{m.body}</div>
                    </div>
                  ))}
                </div>
                <Textarea className="mt-4" rows={4} placeholder={internal ? "Internal note (not sent)" : "Reply to customer (emailed)"} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="desk-reply-input" />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm"><Switch checked={internal} onCheckedChange={setInternal} data-testid="desk-internal-switch" /> Internal note</label>
                  <button className="btn-navy" disabled={!msg} onClick={() => send()} data-testid="desk-send-btn">{internal ? "Add note" : "Send reply"}</button>
                  {!internal && <button className="btn-outline" disabled={!msg} onClick={() => send("resolved")} data-testid="desk-send-resolve-btn">Send and resolve</button>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const ROLES = ["owner", "admin", "instructor", "checkin", "support", "student"];

export function Users() {
  const { user } = useAuth();
  const { data, loading, reload } = useFetch("/admin/users");
  const [f, setF] = useState({ email: "", role: "checkin" });
  const save = async (email, role) => {
    try { await api.post("/admin/users/role", { email, role }); toast.success("Role updated"); setF({ email: "", role: "checkin" }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageTitle title="Users and roles" sub="Staff sign up as normal, then you grant their role here." />
      <div className="panel mb-4 flex flex-wrap gap-2 p-4">
        <Input className="max-w-xs" placeholder="Email of existing account" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="role-email-input" />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-10 rounded-md border px-3 text-sm" data-testid="role-select">{ROLES.filter((r) => user.role === "owner" || !["owner", "admin"].includes(r)).map((r) => <option key={r}>{r}</option>)}</select>
        <button className="btn-gold" onClick={() => save(f.email, f.role)} disabled={!f.email} data-testid="role-grant-btn">Grant role</button>
      </div>
      {loading ? <Loader /> : (
        <Table head={["Name", "Email", "Role", ""]} empty={data.length === 0}>
          {data.map((u) => (
            <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
              <td className="p-3 font-semibold">{u.name}</td><td className="p-3">{u.email}</td><td className="p-3 uppercase">{u.role}</td>
              <td className="p-3">{u.id !== user.id && <button className="text-xs text-[#C8382E] underline" onClick={() => save(u.email, "student")} data-testid={`revoke-role-${u.id}`}>Revoke staff access</button>}</td>
            </tr>
          ))}
        </Table>
      )}
      <div className="panel mt-4 p-4 text-sm text-[#475569]"><strong>Role guide:</strong> Owner = everything incl. tax settings · Admin = all operations · Instructor = events, rosters, quizzes · Check-in = door scanning and badges · Support = support desk, students, orders (view).</div>
    </>
  );
}

export function Audit() {
  const { data, loading } = useFetch("/admin/audit");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Audit log" sub="Every staff change, newest first." />
      <Table head={["When", "Who", "Action", "Target", "Detail"]} empty={data.length === 0}>
        {data.map((a) => <tr key={a.id} className="border-t"><td className="p-3 whitespace-nowrap">{fmtDateTime(a.created_at)}</td><td className="p-3">{a.user_email}</td><td className="p-3 font-mono-num">{a.action}</td><td className="p-3 font-mono-num text-xs">{a.target}</td><td className="p-3">{a.detail}</td></tr>)}
      </Table>
    </>
  );
}
